/**
 * Battle pacing constants. Centralized so the cartridge-text rhythm + future
 * cinematic timings can be tuned in one place rather than scattered across
 * BattleScene event handlers.
 *
 * `LOG_BEAT_MS` is the canonical wait between consecutive battle-log lines
 * during a multi-line burst (move + super-effective + crit + faint).
 * Mirrors DESIGN.md's `motion-beat: 350ms` token added 2026-05-13 to host
 * the EXP 1 battle text audit; the value is the cartridge-feel midpoint
 * between motion-medium (240ms — too rushed for text) and motion-threshold
 * (440ms — too slow for a friend playtester on a 6-inch screen).
 */
export const LOG_BEAT_MS = 350;

// ──────────────────────────────────────────────────────────────────────
// PR 2 — Cathedral combat cinematic constants.
//
// All values land here so the next /design-review pass can tune them in
// one place without grepping across BattleScene + PartyOrbs + battleHpBar.
// ──────────────────────────────────────────────────────────────────────

/** Super-effective pixel-boom camera shake — synchronous with the damage
 *  event, NOT after the 1000ms HP-drain await (eng review Issue 5A 2026-
 *  05-13). 100ms keeps the punch readable on a 6-inch screen without
 *  feeling like a long jolt. Magnitude ~0.005 of canvas = ~4px on a 720px
 *  viewport — DESIGN.md cinematics spec line "100ms canvas shake".
 */
export const SHAKE_MS = 100;
export const SHAKE_INTENSITY = 0.005;

/** Targeted-desat overlay on super-effective hits (everything except the
 *  attacker briefly washes out chroma). 250ms is the DESIGN.md value;
 *  composes with the longer faint-sequence desat (1.0 → 0.6 over 600ms)
 *  but only on the opponent-side hit beat — they don't temporally collide
 *  by spec (the faint sequence runs after the |faint| event, the boom
 *  runs at |damage| time). */
export const DESAT_MS = 250;

/** Level-up sprite scale pulse (1.0 → 1.15 → 1.0). 300ms total —
 *  emotional peak on the rarest in-battle cinematic without feeling
 *  draggy. EXP 3 / DESIGN.md motion section. */
export const SCALE_PULSE_MS = 300;

/** Hold on "X used MOVE!" BEFORE the attack animation starts — the
 *  cartridge shows the line, breathes, THEN attacks. It previously sat
 *  AFTER the lunge, so line + impact + drain mushed into one unreadable
 *  moment (playtest round 2: "the text flies by... easy to miss what I
 *  did and what he did"). */
export const MOVE_LINE_HOLD_MS = 550;

/** Dedicated window for the level-up beat: the jingle's audible attack
 *  (~1s of level_up.ogg) + read time for "grew to Lv N!". Before this
 *  existed, exp_gain + level_up + victory music all started within one
 *  frame and the jingle was inaudible under the pile-up (first-friend
 *  playtest 2026-08-02: "no level up fanfare"). One window per
 *  level-up CHAIN — multi-level chains from 3x EXP log extra levels at
 *  LOG_BEAT_MS without re-triggering the jingle. */
export const LEVEL_UP_BEAT_MS = 1200;

/** Lead-orb heartbeat pulse period (<10% HP, last-alive only). Phase-
 *  locked with the existing HP critical pulse on the bar so both surfaces
 *  beat in sync — single 0.8s period drives both. Design ruling 2026-05-13:
 *  any other value (e.g. 1.0s for orb / 0.8s for HP) reads as a broken
 *  polyrhythm. */
export const HEARTBEAT_PERIOD_MS = 800;
